/**
 * Capacitor bridge: platform detection, geolocation (OS GPS only, spec §2.2),
 * push registration. Everything degrades to browser APIs on the web.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { DEV_DRAW, DEV_LOCATION } from '../config.ts';
import type { Position } from './store.ts';

/** Local Android plugin (android/app/src/main/java/app/drawearth/MockLocationPlugin.java); iOS has no mock flag. */
const MockLocation = registerPlugin<{ isMock(): Promise<{ mock: boolean }> }>('MockLocation', {
  web: () => Promise.resolve({ isMock: async () => ({ mock: false }) }),
  ios: () => Promise.resolve({ isMock: async () => ({ mock: false }) }),
});

export async function isMockLocation(): Promise<boolean> {
  try {
    return (await MockLocation.isMock()).mock;
  } catch {
    return false;
  }
}

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function platform(): 'ios' | 'android' | 'web' {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android' ? p : 'web';
}

/** Drawing requires the native app (spec §2.3); dev builds may opt in. */
export function drawingAvailable(): boolean {
  return isNative() || DEV_DRAW;
}

export type PermissionState = 'granted' | 'denied' | 'prompt';

export async function requestLocationPermission(): Promise<PermissionState> {
  if (DEV_LOCATION && !isNative()) return 'granted';
  if (isNative()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    const status = await Geolocation.requestPermissions({ permissions: ['location'] });
    const s = status.location;
    return s === 'granted' ? 'granted' : s === 'denied' ? 'denied' : 'prompt';
  }
  if (!('geolocation' in navigator)) return 'denied';
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (err) => resolve(err.code === err.PERMISSION_DENIED ? 'denied' : 'granted'),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  });
}

export async function checkLocationPermission(): Promise<PermissionState> {
  if (DEV_LOCATION && !isNative()) return 'granted';
  if (isNative()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    const s = (await Geolocation.checkPermissions()).location;
    return s === 'granted' ? 'granted' : s === 'denied' ? 'denied' : 'prompt';
  }
  try {
    const p = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    return (p?.state as PermissionState) ?? 'prompt';
  } catch {
    return 'prompt';
  }
}

/**
 * Watch the device position. Mock-location flags are surfaced when the
 * platform provides them (Android `isMock`); iOS does not expose one.
 */
export async function watchPosition(cb: (p: Position) => void, onError: (msg: string) => void): Promise<() => void> {
  const fixed = DEV_LOCATION;
  if (fixed && !isNative()) {
    cb({ lat: fixed.lat, lng: fixed.lng, accuracy: 5, mock: false, ts: Date.now() });
    const id = setInterval(() => cb({ lat: fixed.lat, lng: fixed.lng, accuracy: 5, mock: false, ts: Date.now() }), 10_000);
    return () => clearInterval(id);
  }
  if (isNative()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    const id = await Geolocation.watchPosition({ enableHighAccuracy: true, timeout: 20_000, maximumAge: 5_000 }, (pos, err) => {
      if (err) {
        onError(err.message ?? 'location unavailable');
        return;
      }
      if (!pos) return;
      void isMockLocation().then((mock) => cb({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, mock, ts: pos.timestamp }));
    });
    return () => {
      void Geolocation.clearWatch({ id });
    };
  }
  if (!('geolocation' in navigator)) {
    onError('geolocation unsupported');
    return () => {};
  }
  const id = navigator.geolocation.watchPosition(
    (pos) => cb({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, mock: false, ts: pos.timestamp }),
    (err) => onError(err.message),
    { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
  );
  return () => navigator.geolocation.clearWatch(id);
}

/** Register for push and hand the token to the API. No-op on the web. */
export async function registerPush(onToken: (platform: 'ios' | 'android', token: string) => Promise<void>, onTap: (link: string | null) => void): Promise<void> {
  if (!isNative()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return;
  await PushNotifications.addListener('registration', (t) => {
    void onToken(platform() as 'ios' | 'android', t.value);
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const link = (action.notification.data as { link?: string } | undefined)?.link ?? null;
    onTap(link);
  });
  await PushNotifications.register();
}

export async function onAppUrlOpen(cb: (url: string) => void): Promise<void> {
  if (!isNative()) return;
  const { App } = await import('@capacitor/app');
  await App.addListener('appUrlOpen', (e) => cb(e.url));
}
